<template>
  <div
    class="onboarding-trial"
    :class="[
      'onboarding-trial--page-' + pageIndex,
      { 'onboarding-trial--trial-by-plan-v2': trialByPlanV2_18369Treatment() },
    ]"
  >
    <div class="onboarding-trial__header">
      <Logo class="onboarding-trial__logo" :withText="true" />
      <button
        class="onboarding-trial__close onboarding-trial__close--header"
        @click="nextOnboardingPage"
      >
        <span class="iconfont iclose-thin"></span>
      </button>
    </div>

    <Layout
      v-if="wait.is('Loading')"
      title=""
      class="onboarding-trial__inner--steps"
    >
      <div class="layout__wrapper">
        <loading-spinner />
      </div>
    </Layout>

    <Transition
      v-else
      name="fade"
      mode="out-in"
      class="onboarding-trial__inner"
    >
      <Layout
        v-if="pageIndex === 0 && page.steps"
        :title="variantTitle"
        :scrollDefaultSlot="true"
        class="onboarding-trial__inner--steps"
      >
        <button
          class="onboarding-trial__close onboarding-trial__close--body"
          @click="nextOnboardingPage"
        >
          <span class="iconfont iclose-thin"></span>
        </button>

        <div
          v-if="trialByPlanV2_18369Treatment()"
          class="onboarding-trial__periods"
        >
          <div class="onboarding-trial__period">
            <img
              src="@/assets.app/images/onboarding/trial-stars.svg"
              class="onboarding-trial__stars"
            />
            <img
              v-if="!trialPolicy?.trial_days"
              src="@/assets.app/images/onboarding/trial-arrow.svg"
              class="onboarding-trial__arrow"
            />
            <div class="onboarding-trial__period-header">
              <h2 class="onboarding-trial__period-title">Annual plan</h2>
              <div class="onboarding-trial__period-text">
                Includes
                <strong>{{ trialDays.annual }}-day free trial</strong>
              </div>
            </div>
            <div class="onboarding-trial__period-steps">
              <StepItem
                v-for="(step, index) in trialSteps.annual"
                :key="index"
                :step="step"
                variant="annual"
              />
            </div>
          </div>

          <Divider class="onboarding-trial__divider" />

          <div
            class="onboarding-trial__period onboarding-trial__period--monthly"
          >
            <div class="onboarding-trial__period-header">
              <h2 class="onboarding-trial__period-title">Monthly plan</h2>
              <div
                class="onboarding-trial__period-text onboarding-trial__period-text--monthly"
              >
                <span v-if="trialDays.monthly > 0">
                  Includes
                  <strong>{{ trialDays.monthly }}-day free trial</strong>
                </span>
                <span v-else>No free trial included</span>
              </div>
            </div>
            <div class="onboarding-trial__period-steps">
              <StepItem
                v-for="(step, index) in trialSteps.monthly"
                :key="index"
                :step="step"
                variant="monthly"
              />
            </div>
          </div>
        </div>

        <div v-else class="onboarding-trial__steps">
          <div
            v-for="(step, index) in page.steps"
            :key="index"
            class="onboarding-trial__step"
          >
            <div class="onboarding-trial__step-icon-wrapper">
              <img
                :src="getImage(step.icon)"
                class="onboarding-trial__step-icon"
              />
            </div>

            <div class="onboarding-trial__step-text-wrapper">
              <p class="onboarding-trial__step-icon-label">{{ step.day }}</p>
              <p class="onboarding-trial__step-text">{{ step.text }}</p>
            </div>
          </div>
        </div>
      </Layout>

      <Layout
        v-else-if="pageIndex === 1"
        :title="variantTitle"
        class="onboarding-trial__inner--reminder"
      >
        <button
          class="onboarding-trial__close onboarding-trial__close--body"
          @click="nextOnboardingPage"
        >
          <span class="iconfont iclose-thin"></span>
        </button>
        <template #aboveTitle>
          <img
            class="onboarding-trial__image"
            :src="pageImage"
            :width="page.image?.width"
            :height="page.image?.height"
          />
        </template>
        <div
          v-if="!trialByPlanV2_18369Treatment()"
          class="onboarding-trial__date"
        >
          <p class="onboarding-trial__date-text">
            &#x1F514; {{ reminderDate() }}
          </p>
        </div>
      </Layout>
    </Transition>

    <div class="onboarding-trial__button-wrapper">
      <AppButton
        v-if="!wait.is('Loading')"
        class="onboarding-trial__button"
        @click="buttonClick"
      >
        {{ trialByPlanV2_18369Treatment() ? 'Continue' : 'Start Free Trial' }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'
import AppButton from '@/app/components/button/AppButton.vue'
import { onboardingPages } from '@/models/onboarding.pages'
import { getImage } from '@/models/onboarding'
import Layout from '../Layout.vue'
import Logo from '@/components/ui/Logo.vue'
import {
  iosExternalLinkPayment16218Experiment,
  trialByPlanV2_18369Experiment,
  trialByPlanV2_18369Variant,
} from '@/services/ab'
import {
  getTrialByPlanV2_18369VariantConfig,
  getTrialPolicy,
  trialByPlanV2_18369Treatment,
} from '@/services/ab/18369_trial_by_plan'
import type {
  PlatformTrialPolicy,
  TrialByPlanExperimentVariantConfig,
} from '@/services/ab/18369_trial_by_plan'
import { platform } from '@/services/platform'
import type { TrialStep } from '@/models/onboarding.types'
import { assertNotNull } from '@/helpers/typing'
import StepItem from '@/app/components/onboarding/trial/StepItem.vue'
import Divider from '@/components/ui/Divider.vue'
import { useWait } from '@/app/composables/useWait'

const props = defineProps<{ nextOnboardingPage: () => void }>()

const pageIndex = ref<number>(0)

const page = computed(() => onboardingPages.trial[pageIndex.value])
const isLastPage = computed(
  () => pageIndex.value + 1 === onboardingPages.trial.length,
)

const pageImage = computed(() => {
  if (page.value.image) {
    return getImage(page.value.image.src)
  }
  return ''
})

const trialDays = computed((): { annual: number; monthly: number } => {
  if (trialConfig.value) {
    /* Before, Android was implicitly treated as a web.

    Why we need a conversion here:
    - platformString used to be only 'web' | 'ios'.
    - It was set with await platform.isNativeIOSApp() ? 'ios' : 'web'.
    - So Android always became 'web'.
    That means trial days on Android came from the 'web' trial config,
    as we don't have a dedicated Android config because we don't start
    trial-by-plan experiment on Android at all.
    https://github.com/shortformhq/main/issues/17361
    */
    const trialPlatform = platformString.value === 'ios' ? 'ios' : 'web'
    return {
      annual: trialConfig.value.annual[trialPlatform].trial_days ?? 0,
      monthly: trialConfig.value.monthly[trialPlatform].trial_days ?? 0,
    }
  }
  return { annual: 0, monthly: 0 }
})

const calculateStepDay = (index: number, trialDays: number): string => {
  if (index === 0) {
    return 'Today'
  }
  if (index === 1) {
    return 'Day ' + String(trialDays - 2)
  }
  return 'Day ' + String(trialDays)
}

const createAnnualSteps = (
  allSteps: TrialStep[],
  trialDays: number,
): TrialStep[] => {
  return allSteps.map((step, index) => ({
    ...step,
    day: calculateStepDay(index, trialDays),
  }))
}

const createMonthlySteps = (
  allSteps: TrialStep[],
  trialDays: number,
): TrialStep[] => {
  if (trialDays === 0) {
    return [
      {
        ...allSteps[allSteps.length - 1],
        day: 'Today',
        text: "Immediately access all of Shortform's content",
      },
    ]
  }

  return allSteps.map((step, index) => ({
    ...step,
    day: calculateStepDay(index, trialDays),
  }))
}

const trialSteps = computed(
  (): { annual: TrialStep[]; monthly: TrialStep[] } => {
    const allSteps = assertNotNull(page.value.steps)

    return {
      annual: createAnnualSteps(allSteps, trialDays.value.annual),
      monthly: createMonthlySteps(allSteps, trialDays.value.monthly),
    }
  },
)

const variantTitle = computed((): string => {
  if (trialPolicy.value && trialPolicy.value.trial_days === null) {
    if (pageIndex.value === 0) {
      return (
        page.value.title +
        '.<br/><span class="onboarding-trial__title-span"> For <u>Annual plans</u> only.</span>'
      )
    }

    return (
      page.value.title +
      '<span class="onboarding-trial__title-span"> For annual plans only.</span>'
    )
  }

  return page.value.title
})

const buttonClick = (): void => {
  if (isLastPage.value) {
    props.nextOnboardingPage()
    return
  }

  pageIndex.value++
}

const reminderDate = (): string => {
  const date = new Date()
  date.setDate(date.getDate() + 3)

  // Format the date to something like `December 18`
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
  })
  return formatter.format(date)
}

const wait = useWait()
const trialPolicy = ref<PlatformTrialPolicy | null>(null)
const trialConfig = ref<TrialByPlanExperimentVariantConfig | null>(null)
const platformString = ref<'web' | 'ios' | 'android'>('web')
onBeforeMount(async () => {
  wait.start('Loading')
  const platformInfo = await platform.deviceInfo()
  platformString.value = platformInfo.platform

  await trialByPlanV2_18369Experiment()
  const variant = trialByPlanV2_18369Variant() || 'A'
  if (variant) {
    trialPolicy.value = await getTrialPolicy(variant, 'monthly')
    trialConfig.value = getTrialByPlanV2_18369VariantConfig(variant)
  }
  if (platformString.value === 'ios') {
    await iosExternalLinkPayment16218Experiment()
  }
  wait.end('Loading')
})
</script>

<style lang="scss" scoped>
$steps-gap-size: 20px;
$step-block-padding: 12px;

.onboarding-trial {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: $c-white;
  @media (min-width: $w-pc-b) {
    background: $c-bg;
  }

  :deep(.onboarding-layout__default-slot) {
    @media (max-width: $w-mobile-b) {
      overflow: visible;
      padding-bottom: 0;
    }
  }

  &--trial-by-plan-v2 {
    :deep(.onboarding-layout__default-slot::before) {
      display: none;
    }

    :deep(.onboarding-trial__title-span) {
      color: $c-blue-4;
    }

    :deep(.onboarding-layout__default-slot > .onboarding-trial__periods) {
      width: 100%;
      max-width: none;
    }

    .onboarding-trial__periods {
      max-width: none;
    }
  }

  &__periods {
    display: flex;
    gap: 40px;
    justify-content: center;
    margin-top: 24px;

    @media only screen and (max-width: $w-tablet-s) {
      flex-direction: column;
      align-items: center;
      gap: 32px;
      margin-top: 43px;
      padding-bottom: calc(var(--onboarding-padding) * 2);
    }

    @media only screen and (max-width: $w-mobile-s) {
      gap: 20px;
      margin-top: 30px;
    }
  }

  &__divider {
    max-width: var(--onboarding-inner-width);

    @media (min-width: $w-tablet-b) {
      align-self: stretch;
      width: 2px;
      height: auto;
      margin-block: 23px;
      @include divider-orientation(vertical);
    }
  }

  &__stars {
    position: absolute;
    top: -13px;
    left: 40px;

    @media (max-width: 360px) {
      left: 0;
    }
  }

  &__arrow {
    position: absolute;
    top: -18px;
    right: 20px;
    width: 54px;

    @media only screen and (max-width: $w-tablet-s) {
      top: -38px;
      right: 30px;
    }

    @media only screen and (max-width: $w-mobile-s) {
      top: -30px;
    }

    @media (max-width: 360px) {
      right: 0;
    }
  }

  &__period {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      width: 100%;
      max-width: 240px;
      height: 100%;
      transform: translateX(-50%);
      background: #{rgba($c-blue-4, 0.1)};
      border-radius: 16px;

      @media (min-width: $w-mobile-b) {
        max-width: 260px;
      }
    }
    padding-top: 24px;
    padding-bottom: 28px;
    text-align: center;
    position: relative;

    &--monthly {
      position: relative;
      &::before {
        background: $c-bg;
      }
    }

    &-steps {
      display: flex;
      flex-direction: column;
      gap: 16px;
      max-width: 360px;
      margin-top: 16px;

      @media (max-width: $w-mobile-s) {
        margin-top: 8px;
      }
    }

    &-title {
      font-size: 24px;
      font-weight: 500;
      line-height: 28px;
      letter-spacing: -0.48px;

      @media (max-width: $w-mobile-s) {
        font-size: 20px;
        line-height: 24px;
      }
    }

    &-text {
      font-size: 16px;
      font-weight: 300;
      line-height: 28px;
      color: $c-blue-4;

      @media (max-width: $w-mobile-s) {
        font-size: 14px;
      }

      &--monthly {
        color: $c-black-3;
      }
    }

    &-header {
      position: relative;
      z-index: 1;
    }
  }

  &__header {
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    min-height: 88px;
    padding-top: 16px;
    padding-inline: var(--onboarding-padding);
    @media only screen and (max-height: 650px) {
      min-height: auto;
    }
    @media only screen and (min-width: 360px) {
      padding-inline: 38px;
    }
    @media (min-width: $w-pc-b) {
      justify-content: center;
      padding-block: 32px;
    }
  }

  &__logo {
    display: none;
    @media (min-width: $w-pc-b) {
      display: block;
    }
  }

  &__close {
    height: 32px;
    font-size: 32px;
    color: $c-black-2;
  }

  &__close--header {
    @media (min-width: $w-pc-b) {
      display: none;
    }
  }

  &__close--body {
    display: none;
    @media (min-width: $w-pc-b) {
      position: absolute;
      top: 32px;
      right: 32px;
      display: block;
      width: fit-content;
    }
  }

  :deep(.onboarding-layout__text) {
    max-width: var(--onboarding-inner-width);
  }

  &__inner {
    position: relative;
    background: $c-white;
    padding-top: 90px;
    border-radius: 20px 20px 0 0;

    :deep(.onboarding-layout__title) {
      text-align: center;
    }
  }

  &--page-0 :deep(.onboarding-trial__inner--steps) {
    @media (max-width: $w-mobile-b) {
      padding-top: 20px;
    }
  }

  &__inner--steps {
    padding-bottom: 0;

    :deep(.onboarding-layout__title) {
      font-size: 32px;
      line-height: 36px;
      letter-spacing: -0.02em;
      text-wrap: balance;
      color: $c-black-2;
    }
  }

  &__steps {
    display: flex;
    flex-direction: column;
    gap: $steps-gap-size;
    margin-top: 20px;
    @media (max-width: 360px) {
      position: relative;
      left: calc(0px - var(--onboarding-padding));
      width: calc(100% + (var(--onboarding-padding) * 2));
      max-width: none;
      padding-inline: var(--onboarding-padding);
      padding-bottom: calc(var(--onboarding-padding) * 2);
      overflow-y: auto;
    }
  }

  &__step {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: $step-block-padding 16px;
    border-radius: 20px;
    background: $c-white;
    filter: $shadow-f;
    --display-vertical-line: block;

    &:last-child {
      --display-vertical-line: none;
    }

    &:nth-child(odd) {
      --step-border-color: #{$c-yellow};
      --step-bg-color: #{rgba($c-blue-4, 0.1)};
    }

    &:nth-child(even) {
      --step-border-color: #{$c-blue-4};
      --step-bg-color: #{$c-yellow-2};
    }
  }

  &__step-icon-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    width: 68px;
    height: 68px;
    flex-shrink: 0;
    border-radius: 12px;
    background: var(--step-bg-color);

    // Vertical line
    &:not(:last-child):after {
      content: '';
      position: absolute;
      bottom: -1px - $step-block-padding - $steps-gap-size;
      left: 50%;
      display: var(--display-vertical-line);
      width: 2px;
      height: $steps-gap-size;
      background: $c-border-7;
    }
  }

  &__step-icon {
    font-size: 28px;
    width: 28px;
    height: 28px;
  }

  &__step-icon-label {
    margin: 0;
    font-size: 18px;
    line-height: 28px;
    font-weight: 500;
    color: $c-black-15;
  }

  &__step-text {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.02em;
    color: $c-black-3;
  }

  &__inner--reminder {
    gap: 16px;

    :deep(.onboarding-layout__above-title) {
      display: flex;
      justify-content: center;
      flex: 1;
      min-height: 120px;
      margin-bottom: 12px;
    }

    :deep(.onboarding-layout__text) {
      max-width: var(--onboarding-outer-width);
    }

    :deep(.onboarding-layout__default-slot) {
      display: flex;
      flex-direction: column;
      align-items: center;
      flex: 1;
      padding-top: 32px;
      @media only screen and (max-height: 650px) {
        padding-top: 16px;
      }

      &::before {
        content: '';
        width: 35px;
        height: 2px;
        margin-bottom: 32px;
        border-radius: 2px;
        background: $c-border-7;
        @media only screen and (max-height: 650px) {
          margin-bottom: 16px;
        }
      }
    }
  }

  &__image {
    max-height: 100%;
  }

  &__date {
    position: relative;
    padding: 4px 20px;
    border-radius: 8px;
    background: $c-border-7;
    width: fit-content;
  }

  &__date-text {
    margin: 0;
    font-size: 14px;
    line-height: 28px;
    font-weight: 500;
    color: $c-black-15;
  }

  &__button-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    width: 100%;
    padding: var(--onboarding-padding);
    padding-top: 0;
    background: transparent;

    &:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: 0;
      display: block;
      width: 100%;
      height: calc(100% + var(--onboarding-padding));
      background: $c-white;
      mask: linear-gradient(
        0deg,
        rgba(0, 0, 0, 1) 0%,
        rgba(0, 0, 0, 1) 65%,
        rgba(0, 0, 0, 0) 100%
      );
    }
  }

  &__button {
    z-index: 1;
    display: flex;
    width: 100%;
    margin-top: calc(var(--onboarding-padding) * -1);
    max-width: var(--onboarding-inner-width);
    height: 56px;
    font-size: 18px;
    background-color: $c-blue-4;
    color: $c-white;
    box-shadow: 0 0 0 0 rgba($c-blue-4, 1);
    animation: button-pulse 1s linear infinite;

    @include hover {
      background-color: darken($c-blue-4, 8%);
    }
  }
}

.onboarding-trial--trial-by-plan-v2.onboarding-trial--page-0 {
  @media only screen and (max-width: $w-medium-s) {
    @supports (padding-top: env(safe-area-inset-top)) {
      padding-top: calc(env(safe-area-inset-top) + 10px);
    }
  }

  :deep(.onboarding-layout__default-slot) {
    overflow: visible;
  }

  :deep(.onboarding-layout__title) {
    @media (max-width: $w-mobile-s) {
      font-size: 28px;
    }
  }
}

.onboarding-trial--trial-by-plan-v2.onboarding-trial--page-0 .onboarding-trial {
  &__header {
    @media (max-width: $w-pc-s) {
      margin-bottom: -88px;
      z-index: 99;
    }

    @media (max-width: $w-mobile-s) {
      align-items: start;
      min-height: 56px;
      margin-bottom: -56px;
    }
  }

  &__inner {
    padding-top: 60px;
  }
}

@keyframes button-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba($c-blue-4, 1);
  }
  20% {
    box-shadow: 0 0 0 3px rgba($c-blue-4, 0.5);
  }
  100% {
    box-shadow: 0 0 0 24px rgba($c-blue-4, 0);
  }
}
</style>
